import { ChamferOutline } from "@/components/chamfer";
import { useEffect, useMemo, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { DashboardScheduleEditAvailabilityButton } from "./DashboardScheduleEditAvailabilityButton";
import { DashboardSchedulePanelViewToggle } from "./DashboardSchedulePanelViewToggle";
import { DashboardScheduleWeekEvent } from "./DashboardScheduleWeekEvent";
import { DashboardScheduleWeekTasksBand } from "./DashboardScheduleWeekTasksBand";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import { cn } from "@/lib/utils";
import {
  DashboardInterview,
  DashboardSchedulePanelView,
  DashboardScheduleWeekDay,
  DashboardTask,
} from "@/types";

// Hour the grid is scrolled to on open, so it lands on the morning not midnight.
const DEFAULT_SCROLL_HOUR = 7;

export const ScheduleCalendarExpanded = ({
  weekStart,
  interviews,
  tasks,
  panelView,
  onPanelViewChange,
  onEditAvailability,
  onPrevWeek,
  onNextWeek,
  onOpenTasksView,
  showInterviewOwnerAvatars = false,
  onSelectCandidacy,
}: {
  weekStart: Date;
  interviews: DashboardInterview[];
  tasks: DashboardTask[];
  panelView: DashboardSchedulePanelView;
  onPanelViewChange: (view: DashboardSchedulePanelView) => void;
  onEditAvailability: () => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onOpenTasksView: (day: Date) => void;
  showInterviewOwnerAvatars?: boolean;
  // Opens the candidate sheet from an event's popover card name.
  onSelectCandidacy: (interview: DashboardInterview) => void;
}) => {
  const days = useMemo(
    () => dashboardScheduleWeek.weekDays(weekStart),
    [weekStart],
  );
  const interviewsByDay = useMemo(
    () => dashboardScheduleWeek.groupInterviewsByDayKey(interviews),
    [interviews],
  );
  const hourLabels = dashboardScheduleWeek.hourLabels();
  const gridHeightPx = dashboardScheduleWeek.gridHeightPx();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to the morning (not midnight) whenever the grid mounts or the week changes.
  const weekStartMs = weekStart.getTime();
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop =
      DEFAULT_SCROLL_HOUR * dashboardScheduleWeek.HOUR_HEIGHT_PX;
  }, [weekStartMs]);

  // Which month(s) the visible week falls in — the only cue that week-nav has
  // crossed into a different month.
  const startMonth = days[0].date.toLocaleDateString("en-US", {
    month: "short",
  });
  const endMonth = days[days.length - 1].date.toLocaleDateString("en-US", {
    month: "short",
  });
  const monthLabel =
    startMonth === endMonth ? startMonth : `${startMonth}–${endMonth}`;

  const renderWeekNav = () => (
    <Column className="items-center gap-0.5">
      <Text className="lever-calendar-date font-lever-accent text-[9px] font-semibold uppercase tracking-wide text-lever-text-label">
        {monthLabel}
      </Text>
      <Row className="items-center justify-center">
        <Button
          type="button"
          variant="ghost"
          onClick={onPrevWeek}
          className="h-5 w-5 shrink-0 p-0 text-lever-text-label hover:text-lever-text-footnote"
        >
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onNextWeek}
          className="h-5 w-5 shrink-0 p-0 text-lever-text-label hover:text-lever-text-footnote"
        >
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </Button>
      </Row>
    </Column>
  );

  const renderDayHeaderCell = (day: DashboardScheduleWeekDay) => (
    <TableHead
      key={day.key}
      className={cn(
        "px-1 py-0 text-center",
        day.isToday && "bg-lever-surface-selected",
      )}
    >
      <Row className="items-center justify-center gap-2">
        <Text
          className={cn(
            "text-[11px] font-medium",
            day.isToday ? "text-lever-text-action" : "text-lever-text-label",
          )}
        >
          {day.isToday ? "Today" : day.weekdayLabel}
        </Text>
        <Text
          className={cn(
            "lever-calendar-date font-lever-accent text-xs font-semibold tabular-nums",
            day.isToday ? "text-lever-text-action" : "text-lever-text-footnote",
          )}
        >
          {day.date.getDate().toString()}
        </Text>
      </Row>
    </TableHead>
  );

  const renderHeader = () => (
    <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
      <colgroup>
        <col
          style={{
            width: `${dashboardScheduleWeek.TIME_GUTTER_WIDTH_PX.toString()}px`,
          }}
        />
        <col span={7} />
      </colgroup>
      <TableHeader className="lever-table-heading sticky top-0 z-20 bg-lever-surface-default-on-elevation [&_th]:h-12 [&_th]:border-b [&_th]:border-lever-border-light">
        <TableRow>
          <TableHead className="px-0 py-0">{renderWeekNav()}</TableHead>
          {days.map(renderDayHeaderCell)}
        </TableRow>
      </TableHeader>
    </table>
  );

  const renderDayColumn = (day: DashboardScheduleWeekDay) => {
    const eventLayouts = dashboardScheduleWeek.getEventLayouts(
      interviewsByDay.get(day.key) ?? [],
    );

    return (
      <Column
        key={day.key}
        className="relative min-w-0 border-l border-lever-border-light"
        style={{ height: gridHeightPx }}
      >
        {hourLabels
          .filter((hour) => hour > dashboardScheduleWeek.START_HOUR)
          .map((hour) => (
            <div
              key={hour}
              className="absolute inset-x-0 border-t border-lever-border-light"
              style={{
                top:
                  (hour - dashboardScheduleWeek.START_HOUR) *
                  dashboardScheduleWeek.HOUR_HEIGHT_PX,
              }}
            />
          ))}
        {eventLayouts.map(
          ({ interview, topPx, heightPx, leftPct, widthPct }) => (
            <DashboardScheduleWeekEvent
              key={interview.id}
              interview={interview}
              topPx={topPx}
              heightPx={heightPx}
              leftPct={leftPct}
              widthPct={widthPct}
              showOwnerAvatar={showInterviewOwnerAvatars}
              onSelect={
                interview.candidacy
                  ? () => {
                      onSelectCandidacy(interview);
                    }
                  : undefined
              }
            />
          ),
        )}
      </Column>
    );
  };

  const renderBody = () => (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
    >
      <div className="flex">
        <Column
          className="sticky left-0 shrink-0 bg-lever-surface-default"
          style={{ width: dashboardScheduleWeek.TIME_GUTTER_WIDTH_PX }}
        >
          {hourLabels.map((hour) => (
            <Row
              key={hour}
              className="relative items-start justify-end pr-1.5"
              style={{ height: dashboardScheduleWeek.HOUR_HEIGHT_PX }}
            >
              <Text className="-mt-2 font-lever-suisse text-[10px] tabular-nums text-lever-text-label">
                {dashboardScheduleWeek.formatHourLabel(hour)}
              </Text>
            </Row>
          ))}
        </Column>
        <div
          className="grid min-w-0 flex-1"
          style={{
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            height: gridHeightPx,
          }}
        >
          {days.map(renderDayColumn)}
        </div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <Row className="shrink-0 items-center justify-between border-t border-lever-border-light px-3 py-2.5">
      <DashboardScheduleEditAvailabilityButton
        onClick={onEditAvailability}
        variant="labeled"
      />
      <DashboardSchedulePanelViewToggle
        value={panelView}
        onChange={onPanelViewChange}
      />
    </Row>
  );

  const render = () => (
    <Column className="lever-data-table flex h-full min-h-0 flex-col overflow-hidden rounded-lever-md border border-lever-border-light bg-lever-surface-default font-lever-suisse">
      <ChamferOutline />
      {renderHeader()}
      <DashboardScheduleWeekTasksBand
        days={days}
        tasks={tasks}
        interviews={interviews}
        onOpenTasksView={onOpenTasksView}
      />
      {renderBody()}
      {renderFooter()}
    </Column>
  );

  return render();
};
